import { createClient, createConfig } from '@hey-api/client-fetch';
import * as contracts from '@dhuttu/contracts';
import { env } from '../env';

const orgSdk = contracts.org;
const adminSdk = contracts.admin;

export type DhuttuApiClient = ReturnType<typeof makeApi>;

/**
 * Create an API client bound to a bearer token getter.
 * Returns namespaced wrappers that inject the client + idempotency key.
 */
export function makeApi(getToken: () => Promise<string>) {
  const client = createClient(createConfig({
    baseUrl: env.apiBaseUrl,
    auth: () => getToken(),
  }));

  const withClient = <T extends Record<string, unknown>>(opts?: T) => ({ ...opts, client } as T & { client: typeof client });
  const withMutationClient = <T extends Record<string, unknown>>(opts: T) => ({
    ...opts,
    client,
    headers: {
      'Idempotency-Key': crypto.randomUUID(),
      ...(opts as any).headers,
    },
  } as T & { client: typeof client });

  return {
    org: {
      signup: (opts: Parameters<typeof orgSdk.orgSignup>[0]) => orgSdk.orgSignup(withMutationClient(opts)),
      me: (opts?: Parameters<typeof orgSdk.getOrgMe>[0]) => orgSdk.getOrgMe(withClient(opts)),
      inviteMember: (opts: Parameters<typeof orgSdk.inviteMember>[0]) => orgSdk.inviteMember(withMutationClient(opts)),
      disableMember: (opts: Parameters<typeof orgSdk.disableMember>[0]) => orgSdk.disableMember(withMutationClient(opts)),
      listWarehouses: (opts?: Parameters<typeof orgSdk.listWarehouses>[0]) => orgSdk.listWarehouses(withClient(opts)),
      createWarehouse: (opts: Parameters<typeof orgSdk.createWarehouse>[0]) => orgSdk.createWarehouse(withMutationClient(opts)),
      updateWarehouse: (opts: Parameters<typeof orgSdk.updateWarehouse>[0]) => orgSdk.updateWarehouse(withMutationClient(opts)),
      listStores: (opts?: Parameters<typeof orgSdk.listStores>[0]) => orgSdk.listStores(withClient(opts)),
      createStore: (opts: Parameters<typeof orgSdk.createStore>[0]) => orgSdk.createStore(withMutationClient(opts)),
      updateStore: (opts: Parameters<typeof orgSdk.updateStore>[0]) => orgSdk.updateStore(withMutationClient(opts)),
      generateActivationCode: (opts: Parameters<typeof orgSdk.generateActivationCode>[0]) => orgSdk.generateActivationCode(withMutationClient(opts)),
      revokeStoreDevice: (opts: Parameters<typeof orgSdk.revokeStoreDevice>[0]) => orgSdk.revokeStoreDevice(withMutationClient(opts)),
    },
    admin: {
      listOrgs: (opts?: Parameters<typeof adminSdk.listOrgs>[0]) => adminSdk.listOrgs(withClient(opts)),
      getOrg: (opts: Parameters<typeof adminSdk.getOrg>[0]) => adminSdk.getOrg(withClient(opts)),
      approveOrg: (opts: Parameters<typeof adminSdk.approveOrg>[0]) => adminSdk.approveOrg(withMutationClient(opts)),
      suspendOrg: (opts: Parameters<typeof adminSdk.suspendOrg>[0]) => adminSdk.suspendOrg(withMutationClient(opts)),
      searchAudit: (opts?: Parameters<typeof adminSdk.searchAudit>[0]) => adminSdk.searchAudit(withClient(opts)),
    },
  };
}
